'use client'

import {useEffect, useState} from 'react'
import {Globe, Loader2, RotateCcw, Save} from 'lucide-react'
import type {Account, SettingsState} from '@/types'
import {PageHeader} from '@/components/PageHeader'
import {apiPost} from '@/lib/api'
import {message} from '@/components/Toast'
import {resolveProxyUrl} from '@/lib/proxy'

export function ProxySettingsView({
    settings,
    saveSettings,
    account,
}: {
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => Promise<void> | void
    account: Account | null
}) {
    const savedProxyUrl = resolveProxyUrl(settings.proxy || {})
    const [proxyUrl, setProxyUrl] = useState(savedProxyUrl)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)

    useEffect(() => {
        setProxyUrl(savedProxyUrl)
    }, [savedProxyUrl])

    const normalizedProxyUrl = proxyUrl.trim()
    const hasChanges = normalizedProxyUrl !== savedProxyUrl

    async function handleSave() {
        if (saving || !account) return
        setSaving(true)
        try {
            await saveSettings({
                proxy: {
                    proxyUrl: normalizedProxyUrl,
                    proxyMode: 'custom',
                    portNestId: '',
                    proxyIp: '',
                    proxyUser: '',
                    proxyPassword: '',
                },
            })
            message.success('代理设置已保存')
        } catch {
            message.error('保存失败')
        } finally {
            setSaving(false)
        }
    }

    async function handleClear() {
        if (saving || !account) return
        setSaving(true)
        try {
            setProxyUrl('')
            await saveSettings({
                proxy: {
                    proxyUrl: '',
                    proxyMode: 'custom',
                    portNestId: '',
                    proxyIp: '',
                    proxyUser: '',
                    proxyPassword: '',
                },
            })
            message.success('代理已清除')
        } catch {
            message.error('清除失败')
        } finally {
            setSaving(false)
        }
    }

    async function handleTest() {
        if (testing || !normalizedProxyUrl) return
        setTesting(true)
        try {
            const result = await apiPost<{ success: boolean; message: string }>(
                '/proxy/test',
                {proxyUrl: normalizedProxyUrl},
            )
            message.success(result.message || '代理连通正常')
        } catch {
            message.error('代理连接失败，请检查代理是否有效')
        } finally {
            setTesting(false)
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="代理设置"
                description="修改当前微信号绑定的代理 URL。"
            />
            <div className="flex-1 overflow-y-auto p-6">
                <div className="bg-white rounded-card shadow-card border border-hair w-full max-w-2xl p-8">
                    <div className="flex items-center gap-3 mb-8 pb-6 border-b border-hair">
                        <Globe size={24} className="text-accent"/>
                        <h3 className="text-lg font-medium text-ink tracking-wide">微信代理</h3>
                    </div>

                    <label className="block text-sm font-medium text-soft mb-1.5">
                        代理 URL
                    </label>
                    <input
                        type="text"
                        value={proxyUrl}
                        onChange={(event) => setProxyUrl(event.target.value)}
                        placeholder="socks5://user:pass@127.0.0.1:7890"
                        disabled={!account}
                        className="w-full px-4 py-2.5 border border-hair rounded-lg text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors disabled:bg-gray-50 disabled:text-muted"
                    />

                    <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-hair">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || !account || !hasChanges}
                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent text-white rounded-lg font-medium text-sm hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                            保存设置
                        </button>
                        <button
                            type="button"
                            onClick={handleTest}
                            disabled={testing || !normalizedProxyUrl}
                            className="inline-flex items-center gap-2 px-6 py-2.5 border border-accent text-accent rounded-lg font-medium text-sm hover:bg-accent/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {testing && <Loader2 size={14} className="animate-spin"/>}
                            测试代理
                        </button>
                        {savedProxyUrl ? (
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={saving || !account}
                                className="inline-flex items-center gap-2 px-6 py-2.5 border border-hair text-soft rounded-lg font-medium text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <RotateCcw size={14}/>
                                清除代理
                            </button>
                        ) : null}
                    </div>

                    {savedProxyUrl ? (
                        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-700 break-all">
                                <span className="font-medium">当前代理：</span>
                                {savedProxyUrl}
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    )
}
